// screens/SettingsScreen.js - All 8 settings tabs
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { SyncBar, ConfirmDialog, SectionHeader } from '../components/shared';
import { useLang } from '../contexts/LanguageContext';
import { BottomNav } from './HomeScreen';
import ThemeToggle from '../components/ThemeToggle';
import LanguageToggle from '../components/LanguageToggle';
import api, { getErrorMessage } from '../utils/api';

const TABS_ADMIN = ['Users', 'Models', 'Server', 'Photo Quality', 'Storage', 'Schedule', 'Sites', 'Checklists', 'Performance'];
// Schedule (report scheduling + SMTP) moved to Admin-only per client request.
const TABS_SUPERVISOR = ['Server', 'Photo Quality', 'Storage'];

export default function SettingsScreen() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = user?.role === 'Admin';
  const isSupervisorPlus = ['Admin', 'Supervisor'].includes(user?.role);
  // Photographers get no server/config tabs — the server address is not theirs to change.
  const tabs = isAdmin ? TABS_ADMIN : isSupervisorPlus ? TABS_SUPERVISOR : [];
  const [activeTab, setActiveTab] = useState(tabs[0] || null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={() => navigate(-1)}>←</button>
        <h2>Settings</h2>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <LanguageToggle />
          <ThemeToggle />
          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--accent-danger)' }} onClick={() => logout()}>Sign out</button>
        </div>
      </div>

      {tabs.length > 0 && (
        <div style={{ padding: '0 16px' }}>
          <div className="settings-tabs">
            {tabs.map(tab => (
              <div key={tab} className={`settings-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}>
                {tab}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="page-content" style={{ paddingTop: tabs.length ? 0 : 16 }}>
        {tabs.length === 0 && (
          <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>👤</div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{user?.full_name}</div>
            <div style={{ fontSize: '0.8125rem', marginTop: 2 }}>{user?.role}</div>
            <div style={{ fontSize: '0.8125rem', marginTop: 12 }}>
              Use the theme toggle above to switch light/dark. Contact your administrator for account changes.
            </div>
          </div>
        )}
        {activeTab === 'Users' && isAdmin && <UsersTab />}
        {activeTab === 'Models' && isAdmin && <ModelsTab />}
        {activeTab === 'Server' && <ServerTab />}
        {activeTab === 'Photo Quality' && isSupervisorPlus && <PhotoQualityTab />}
        {activeTab === 'Storage' && isSupervisorPlus && <StorageTab />}
        {activeTab === 'Schedule' && isAdmin && <ScheduleTab />}
        {activeTab === 'Sites' && isAdmin && <SitesTab />}
        {activeTab === 'Checklists' && isAdmin && <ChecklistsTab />}
        {activeTab === 'Performance' && isAdmin && <PerformanceTab />}
      </div>

      <BottomNav active="settings" />
    </div>
  );
}

// ── Users Tab ─────────────────────────────────────────────────────────────────
const USER_ROLES = ['Admin', 'Supervisor', 'Reviewer', 'Photographer', 'R&D'];

// Generate a password that satisfies the server policy (12+ chars, upper, lower,
// number, special) and stays readable — ambiguous characters are left out.
function generatePassword() {
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ', L = 'abcdefghijkmnpqrstuvwxyz', D = '23456789', S = '!@#$%*';
  const pick = (s) => s[Math.floor(Math.random() * s.length)];
  return pick(U) + pick(L) + pick(L) + pick(L) + pick(L) + '-' +
    pick(D) + pick(D) + pick(D) + pick(S) + pick(L) + pick(L);
}

function UsersTab() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  // modal: { mode: 'new' | 'edit' | 'reset', user? }
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Credentials to hand over after a create/reset (no email is sent).
  const [creds, setCreds] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    const [u, s] = await Promise.all([api.get('/api/users'), api.get('/api/sites')]);
    setUsers(u.data); setSites(s.data); setLoading(false);
  };

  const openNew = () => {
    setForm({ full_name: '', username: '', password: generatePassword(), role: 'Photographer', site_id: '', email: '' });
    setModal({ mode: 'new' });
  };
  const openEdit = (u) => {
    setForm({ full_name: u.full_name, role: u.role, site_id: u.site_id || '', email: u.email || '' });
    setModal({ mode: 'edit', user: u });
  };
  const openReset = (u) => {
    setForm({ password: generatePassword() });
    setModal({ mode: 'reset', user: u });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'new') {
        await api.post('/api/users', { ...form, site_id: form.site_id || null });
        setCreds({ full_name: form.full_name, username: form.username.toLowerCase(), password: form.password });
        toast.success('User created');
      } else if (modal.mode === 'edit') {
        await api.put(`/api/users/${modal.user.id}`, {
          full_name: form.full_name, role: form.role, site_id: form.site_id || null, email: form.email || null,
        });
        toast.success('User updated');
      } else if (modal.mode === 'reset') {
        await api.post(`/api/users/${modal.user.id}/reset-password`, { new_password: form.password });
        setCreds({ full_name: modal.user.full_name, username: modal.user.username, password: form.password });
        toast.success('Password reset');
      }
      setModal(null);
      loadData();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (u) => {
    try {
      await api.put(`/api/users/${u.id}`, { active: u.active ? 0 : 1 });
      toast.success(u.active ? 'User deactivated' : 'User activated');
      loadData();
    } catch { toast.error('Failed to update user'); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success('Copied'), () => {});
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner spinner-lg" /></div>;

  const isReset = modal?.mode === 'reset';
  const isEdit = modal?.mode === 'edit';

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div className="section-title">{users.length} Users</div>
        <button className="btn btn-primary btn-sm" onClick={openNew}>+ New User</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {users.map(u => (
          <div key={u.id} className="card" style={{ padding: '12px 14px', opacity: u.active ? 1 : 0.5 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{u.full_name}</div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  @{u.username} · {u.role} · {u.site_name || 'All sites'}
                </div>
                {u.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{u.email}</div>}
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {u.must_change_password ? '⏳ Awaiting first login (temp password)'
                    : u.last_login ? `Last login: ${new Date(u.last_login).toLocaleDateString()}` : 'Never logged in'}
                </div>
              </div>
              <span className={`badge ${u.active ? 'badge-success' : 'badge-secondary'}`} style={{ flexShrink: 0 }}>
                {u.active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => openEdit(u)}>✏️ Edit</button>
              <button className="btn btn-secondary btn-sm" onClick={() => openReset(u)}>🔑 Reset password</button>
              <button className="btn btn-ghost btn-sm" onClick={() => handleToggleActive(u)}>
                {u.active ? '🚫 Deactivate' : '✓ Activate'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* New / Edit / Reset modal */}
      {modal && (
        <div className="overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 20 }}>
              {modal.mode === 'new' ? 'New User' : isEdit ? `Edit — ${modal.user.full_name}` : `Reset password — ${modal.user.full_name}`}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {!isReset && (
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input type="text" value={form.full_name || ''} className="input-field" placeholder="Jane Smith"
                    onChange={e => setForm(v => ({ ...v, full_name: e.target.value }))} />
                </div>
              )}
              {modal.mode === 'new' && (
                <div className="form-group">
                  <label className="form-label">Username *</label>
                  <input type="text" value={form.username || ''} className="input-field" placeholder="jsmith"
                    autoCapitalize="none" autoCorrect="off"
                    onChange={e => setForm(v => ({ ...v, username: e.target.value }))} />
                </div>
              )}
              {(modal.mode === 'new' || isReset) && (
                <div className="form-group">
                  <label className="form-label">{isReset ? 'New temporary password *' : 'Temporary password *'}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input type="text" value={form.password || ''} className="input-field" style={{ fontFamily: 'var(--font-mono)' }}
                      onChange={e => setForm(v => ({ ...v, password: e.target.value }))} />
                    <button className="btn btn-secondary btn-sm" onClick={() => setForm(v => ({ ...v, password: generatePassword() }))}>↻ Generate</button>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    The user must set their own password on first login.
                  </div>
                </div>
              )}
              {!isReset && (
                <>
                  <div className="form-group">
                    <label className="form-label">Role *</label>
                    <select value={form.role || 'Photographer'} className="input-field"
                      onChange={e => setForm(v => ({ ...v, role: e.target.value }))}>
                      {USER_ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Site</label>
                    <select value={form.site_id || ''} className="input-field"
                      onChange={e => setForm(v => ({ ...v, site_id: e.target.value }))}>
                      <option value="">All Sites (Admin)</option>
                      {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email (optional)</label>
                    <input type="email" value={form.email || ''} className="input-field" placeholder="jane@company.com"
                      onChange={e => setForm(v => ({ ...v, email: e.target.value }))} />
                  </div>
                </>
              )}
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="btn btn-secondary btn-full" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary btn-full" onClick={handleSubmit} disabled={saving}>
                  {saving ? 'Saving...' : modal.mode === 'new' ? 'Create User' : isReset ? 'Reset password' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credentials hand-over (shown after create/reset — no email is sent) */}
      {creds && (
        <div className="overlay" onClick={() => setCreds(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 6 }}>✅ Give these login details to {creds.full_name}</h3>
            <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              No email is sent. Share these with the user — they pick their own password on first login.
            </p>
            {[
              { label: 'Username', value: creds.username },
              { label: 'Temporary password', value: creds.password },
            ].map(row => (
              <div key={row.label} className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">{row.label}</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input readOnly value={row.value} className="input-field" style={{ fontFamily: 'var(--font-mono)' }}
                    onFocus={e => e.target.select()} />
                  <button className="btn btn-secondary btn-sm" onClick={() => copy(row.value)}>Copy</button>
                </div>
              </div>
            ))}
            <button className="btn btn-primary btn-full" style={{ marginTop: 8 }}
              onClick={() => copy(`PhotoApp login\nUsername: ${creds.username}\nPassword: ${creds.password}\nphotoqa.nl`)}>
              Copy both
            </button>
            <button className="btn btn-ghost btn-full" style={{ marginTop: 8 }} onClick={() => setCreds(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Models Tab ────────────────────────────────────────────────────────────────
// Curated list of product models shown in the New Session dropdown.
const MODEL_FACTORIES = ['CR8TIF EU', 'DREWPOL', 'Both'];
function ModelsTab() {
  const toast = useToast();
  const [models, setModels] = useState([]);
  const [newName, setNewName] = useState('');
  const [newFactory, setNewFactory] = useState('');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name }

  useEffect(() => { load(); }, []);
  const load = async () => {
    try { const { data } = await api.get('/api/product-models', { params: { all: 1 } }); setModels(data); }
    catch {} finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post('/api/product-models', { name: newName.trim(), factory: newFactory || null });
      toast.success('Model added');
      setNewName('');
      setNewFactory('');
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setAdding(false); }
  };

  const setFactory = async (m, factory) => {
    try {
      await api.put(`/api/product-models/${m.id}`, { factory: factory || null });
      setModels(list => list.map(x => x.id === m.id ? { ...x, factory: factory || null } : x));
    } catch { toast.error('Failed to set factory'); }
  };

  const saveEdit = async () => {
    const name = editing.name.trim();
    if (!name) return;
    try {
      await api.put(`/api/product-models/${editing.id}`, { name });
      toast.success('Model updated');
      setEditing(null);
      load();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const toggle = async (m) => {
    try {
      await api.put(`/api/product-models/${m.id}`, { active: m.active ? 0 : 1 });
      load();
    } catch { toast.error('Failed to update'); }
  };

  const remove = async (m) => {
    try { await api.delete(`/api/product-models/${m.id}`); toast.success('Model removed'); load(); }
    catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner spinner-lg" /></div>;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Add Model" />
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
            className="input-field" placeholder="Model name" style={{ flex: 1, minWidth: 160 }}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
          <select value={newFactory} onChange={e => setNewFactory(e.target.value)}
            className="input-field" style={{ width: 'auto', minWidth: 130 }}>
            <option value="">Factory…</option>
            {MODEL_FACTORIES.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !newName.trim()}>
            {adding ? '...' : 'Add'}
          </button>
        </div>
        <div className="form-hint" style={{ marginTop: 8 }}>
          These appear (A→Z) in the New Session model dropdown. The factory is carried onto the order. Photographers can still log an undocumented model.
        </div>
      </div>

      <div className="section-title">Models ({models.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {models.map(m => (
          <div key={m.id} className="card" style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, opacity: m.active ? 1 : 0.5 }}>
            {editing?.id === m.id ? (
              <>
                <input type="text" value={editing.name} autoFocus
                  onChange={e => setEditing(s => ({ ...s, name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                  className="input-field" style={{ flex: 1 }} />
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: '0.9375rem' }}>{m.name}</span>
                <select value={m.factory || ''} onChange={e => setFactory(m, e.target.value)}
                  className="input-field" style={{ width: 'auto', minWidth: 120, padding: '4px 8px', fontSize: '0.8125rem' }}
                  title="Factory">
                  <option value="">No factory</option>
                  {MODEL_FACTORIES.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <button className="btn btn-ghost btn-sm" title="Edit name" onClick={() => setEditing({ id: m.id, name: m.name })}>✏️</button>
                <div className={`toggle ${m.active ? 'on' : ''}`} onClick={() => toggle(m)} style={{ cursor: 'pointer' }} />
                <button className="btn btn-ghost btn-sm" title="Remove" style={{ color: 'var(--accent-danger)' }} onClick={() => remove(m)}>🗑</button>
              </>
            )}
          </div>
        ))}
        {models.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>No models yet</div>}
      </div>
    </div>
  );
}

// ── Server Tab ────────────────────────────────────────────────────────────────
function ServerTab() {
  const toast = useToast();
  const { user } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({});
  const isAdmin = user?.role === 'Admin';

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/api/settings');
      setSettings(data); setForm(data);
    } catch {} finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    setShowConfirm(false);
    try {
      await api.patch('/api/settings', {
        updates: {
          server_internal_address: form.server_internal_address,
          server_external_address: form.server_external_address,
        }
      });
      toast.success('Server settings saved');
      loadSettings();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const { isOnline } = window.navigator.onLine ? { isOnline: true } : { isOnline: false };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Server Addresses" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Internal Address</label>
            <input type="url" value={form.server_internal_address || ''} disabled={!isAdmin}
              onChange={e => setForm(v => ({ ...v, server_internal_address: e.target.value }))}
              className="input-field" placeholder="http://192.168.1.100:3001" />
            <div className="form-hint">Used within the company network (Wi-Fi)</div>
          </div>
          <div className="form-group">
            <label className="form-label">External Address</label>
            <input type="url" value={form.server_external_address || ''} disabled={!isAdmin}
              onChange={e => setForm(v => ({ ...v, server_external_address: e.target.value }))}
              className="input-field" placeholder="https://photos.company.com" />
            <div className="form-hint">Used outside the company network</div>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setShowConfirm(true)} disabled={saving}>
              {saving ? 'Saving...' : 'Save Server Settings'}
            </button>
          )}
        </div>
      </div>

      {/* Sync log */}
      <div className="card">
        <SectionHeader title="Sync Log" />
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
          <div>{new Date().toLocaleTimeString()} — Server reachable ✓</div>
          <div>{new Date().toLocaleTimeString()} — 0 photos pending sync</div>
        </div>
      </div>

      <ConfirmDialog open={showConfirm}
        title="⚠ Change Server Address?"
        message="Changing the server address will affect all users. Make sure the new address is correct before saving. This action is logged."
        confirmLabel="Save Changes"
        onConfirm={handleSave}
        onCancel={() => setShowConfirm(false)}
      />
    </div>
  );
}

// ── Photo Quality Tab ─────────────────────────────────────────────────────────
function PhotoQualityTab() {
  const toast = useToast();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try { const { data } = await api.get('/api/settings'); setSettings(data); }
    catch {} finally { setLoading(false); }
  };

  const save = async (updates) => {
    setSaving(true);
    try {
      await api.patch('/api/settings', { updates });
      setSettings(s => ({ ...s, ...updates }));
      toast.success('Settings saved');
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const estimateSize = (quality, resolution) => {
    const mp = { '8mp': 8, '12mp': 12, '16mp': 16, '20mp': 20, 'full': 24 };
    const q = { 'standard': 0.75, 'high': 0.85, 'maximum': 0.95 };
    const megapixels = mp[resolution] || 12;
    const qualityFactor = q[quality] || 0.85;
    const mb = (megapixels * 0.7 * qualityFactor).toFixed(1);
    return (mb * 6).toFixed(0);
  };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  const QualitySection = ({ dept, label }) => {
    const resKey = `photo_resolution_${dept}`;
    const fmtKey = `photo_format_${dept}`;
    const qualKey = `photo_quality_${dept}`;

    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title={label} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="form-group">
            <label className="form-label">Resolution</label>
            <select value={settings[resKey] || '12mp'} onChange={e => save({ [resKey]: e.target.value })} className="input-field">
              <option value="8mp">8 MP</option>
              <option value="12mp">12 MP (Production default)</option>
              <option value="16mp">16 MP (R&D default)</option>
              <option value="20mp">20 MP</option>
              <option value="full">Full camera resolution</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Format</label>
            <select value={settings[fmtKey] || 'jpeg'} onChange={e => save({ [fmtKey]: e.target.value })} className="input-field">
              <option value="jpeg">JPEG (recommended)</option>
              <option value="heic">HEIC</option>
            </select>
            {settings[fmtKey] === 'heic' && (
              <div className="warning-banner" style={{ marginTop: 8 }}>
                ⚠ HEIC files may not open on all devices. Consider JPEG for customer exports.
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">JPEG Quality</label>
            <select value={settings[qualKey] || '85'} onChange={e => save({ [qualKey]: e.target.value })} className="input-field">
              <option value="75">Standard 75%</option>
              <option value="85">High 85% (default)</option>
              <option value="95">Maximum 95%</option>
            </select>
          </div>
          <div style={{ padding: '10px 14px', background: 'var(--bg-input)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}>
            <span style={{ color: 'var(--text-muted)' }}>Storage estimate: </span>
            <strong>One session (6 photos) ≈ {estimateSize(settings[qualKey], settings[resKey])} MB</strong>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="warning-banner" style={{ marginBottom: 16 }}>
        ⚠ Changes apply to new sessions only. Existing photos are not affected.
      </div>
      <QualitySection dept="production" label="Production" />
      <QualitySection dept="rd" label="R&D" />
    </div>
  );
}

// ── Storage Tab ───────────────────────────────────────────────────────────────
function StorageTab() {
  const [storageInfo, setStorageInfo] = useState(null);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [info, s] = await Promise.all([api.get('/api/storage/info'), api.get('/api/settings')]);
      setStorageInfo(info.data); setSettings(s.data);
    } catch {} finally { setLoading(false); }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const mb = bytes / (1024 * 1024);
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
  };

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Server Storage" />
        {storageInfo && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Production photos</span>
              <span style={{ fontWeight: 600 }}>{storageInfo.production.count} files · {formatBytes(storageInfo.production.bytes)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
              <span style={{ color: 'var(--text-secondary)' }}>R&D photos</span>
              <span style={{ fontWeight: 600 }}>{storageInfo.rd.count} files · {formatBytes(storageInfo.rd.bytes)}</span>
            </div>
          </div>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Local Retention" />
        <div className="form-group">
          <label className="form-label">Keep synced photos locally for</label>
          <select value={settings.local_retention_days || '7'}
            onChange={async e => {
              await api.patch('/api/settings', { updates: { local_retention_days: e.target.value } });
              setSettings(s => ({ ...s, local_retention_days: e.target.value }));
              toast.success('Saved');
            }}
            className="input-field">
            <option value="1">1 day</option>
            <option value="3">3 days</option>
            <option value="7">7 days (default)</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
            <option value="0">Never auto-remove</option>
          </select>
          <div className="form-hint">Only Synced photos are eligible for auto-removal. Pending/Failed photos are kept.</div>
        </div>
      </div>

      <div className="card">
        <SectionHeader title="Archiving Policy" />
        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          Photos are retained for 2 years on the server. Photos approaching the retention limit are flagged for archiving and never removed automatically without admin confirmation.
          Manage the archiving queue with your IT partner.
        </div>
      </div>
    </div>
  );
}

// ── Schedule Tab ──────────────────────────────────────────────────────────────
function ScheduleTab() {
  const toast = useToast();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false);

  useEffect(() => { loadSettings(); }, []);
  const loadSettings = async () => {
    try { const { data } = await api.get('/api/settings'); setSettings(data); }
    catch {} finally { setLoading(false); }
  };

  const save = async (key, value) => {
    try {
      await api.patch('/api/settings', { updates: { [key]: value } });
      setSettings(s => ({ ...s, [key]: value }));
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const [reportLoading, setReportLoading] = useState(false);
  const [reportCount, setReportCount] = useState(null);

  const sendTestEmail = async () => {
    setTestLoading(true);
    try {
      const { data } = await api.post('/api/settings/test-email', {});
      toast.success(`Test email sent to ${data.recipient}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setTestLoading(false); }
  };

  const sendReviewReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await api.post('/api/reports/review/send', {});
      toast.success(`Review report sent to ${data.recipient} (${data.count} photos)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally { setReportLoading(false); }
  };

  const previewReport = async () => {
    try {
      const { data } = await api.get('/api/reports/review/preview');
      setReportCount(data.count);
      toast.info(`${data.count} photo(s) currently To discuss / Feedback requested`);
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Weekly Report Email" />
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Send on</label>
          <select value={settings.weekly_report_day || 'monday'} onChange={e => save('weekly_report_day', e.target.value)} className="input-field">
            {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">SMTP From</label>
          <input type="email" value={settings.smtp_from || ''} onChange={e => save('smtp_from', e.target.value)} className="input-field" placeholder="noreply@company.com" />
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Photographer Weekly Digest" />
        <div className="form-group">
          <label className="form-label">Send badge notification on</label>
          <select value={settings.photographer_digest_day || 'friday'} onChange={e => save('photographer_digest_day', e.target.value)} className="input-field">
            {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Review Report (Review queue)" />
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Recipient</label>
          <input type="email" value={settings.review_report_recipient || ''} onChange={e => save('review_report_recipient', e.target.value)} className="input-field" placeholder="service@cr8tif.nl" />
          <div className="form-hint">Photos still labelled To discuss / Feedback requested, with notes, labels and pin comments.</div>
        </div>
        <div className="form-group" style={{ marginBottom: 14 }}>
          <label className="form-label">Send on</label>
          <select value={settings.review_report_day || 'friday'} onChange={e => save('review_report_day', e.target.value)} className="input-field">
            {DAYS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
          </select>
        </div>
        <label className="toggle-wrap" style={{ marginBottom: 14 }} onClick={() => save('review_report_enabled', settings.review_report_enabled === '1' ? '0' : '1')}>
          <div className={`toggle ${settings.review_report_enabled === '1' ? 'on' : ''}`} />
          <span style={{ fontSize: '0.875rem' }}>Send automatically every week</span>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={previewReport}>
            👁 Preview{reportCount != null ? ` (${reportCount})` : ''}
          </button>
          <button className="btn btn-primary" onClick={sendReviewReport} disabled={reportLoading}>
            {reportLoading ? 'Sending...' : '📧 Send report now'}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="SMTP Configuration" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            { key: 'smtp_host', label: 'SMTP Host', placeholder: 'smtp.company.com' },
            { key: 'smtp_port', label: 'SMTP Port', placeholder: '587' },
            { key: 'smtp_user', label: 'SMTP Username', placeholder: 'user@company.com' },
            { key: 'smtp_pass', label: 'SMTP Password', placeholder: '••••••••', type: 'password' },
          ].map(f => (
            <div key={f.key} className="form-group">
              <label className="form-label">{f.label}</label>
              <input type={f.type || 'text'} value={settings[f.key] || ''}
                onChange={e => save(f.key, e.target.value)}
                className="input-field" placeholder={f.placeholder} />
            </div>
          ))}
        </div>
      </div>

      <button className="btn btn-secondary btn-full" onClick={sendTestEmail} disabled={testLoading}>
        {testLoading ? 'Sending...' : '📧 Send Test Email'}
      </button>
    </div>
  );
}

// ── Sites Tab ─────────────────────────────────────────────────────────────────
function SitesTab() {
  const toast = useToast();
  const { t } = useLang();
  const [uploadSiteId, setUploadSiteId] = useState(null);
  const [sites, setSites] = useState([]);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { id, name, active }

  useEffect(() => { loadSites(); }, []);
  const loadSites = async () => {
    try { const { data } = await api.get('/api/sites'); setSites(data); }
    catch {} finally { setLoading(false); }
  };

  const saveEdit = async () => {
    const name = editing.name.trim();
    if (!name) return;
    try {
      await api.put(`/api/sites/${editing.id}`, { name, active: editing.active });
      toast.success('Site updated');
      setEditing(null);
      loadSites();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await api.post('/api/sites', { name: newName.trim() });
      toast.success('Site created');
      setNewName('');
      loadSites();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setCreating(false); }
  };

  const toggleSite = async (site) => {
    try {
      await api.put(`/api/sites/${site.id}`, { name: site.name, active: site.active ? 0 : 1 });
      loadSites();
    } catch { toast.error('Failed to update site'); }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Add Site" />
        <div style={{ display: 'flex', gap: 8 }}>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)}
            className="input-field" placeholder="Site name" style={{ flex: 1 }}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating || !newName.trim()}>
            {creating ? '...' : 'Add'}
          </button>
        </div>
      </div>

      <div className="section-title">All Sites</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sites.map(s => (
          <React.Fragment key={s.id}>
          <div className="card" style={{ padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            {editing?.id === s.id ? (
              <>
                <input type="text" value={editing.name} autoFocus
                  onChange={e => setEditing(v => ({ ...v, name: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                  className="input-field" style={{ flex: 1 }} />
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>✕</button>
              </>
            ) : (
              <>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Created {new Date(s.created_at).toLocaleDateString()}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{uploadSummary(s, t)}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" title={t('sites.upload.title')}
                    onClick={() => setUploadSiteId(id => (id === s.id ? null : s.id))}>⚙</button>
                  <button className="btn btn-ghost btn-sm" title="Rename" onClick={() => setEditing({ id: s.id, name: s.name, active: s.active })}>✏️</button>
                  <span className={`badge ${s.active ? 'badge-success' : 'badge-secondary'}`}>{s.active ? 'Active' : 'Inactive'}</span>
                  <div className={`toggle ${s.active ? 'on' : ''}`} onClick={() => toggleSite(s)} style={{ cursor: 'pointer' }} />
                </div>
              </>
            )}
          </div>
          {uploadSiteId === s.id && (
            <SiteUploadSettings site={s} onSaved={() => { setUploadSiteId(null); loadSites(); }} />
          )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

function uploadSummary(site, t) {
  const size = `${site.photo_max_side || 1600} px · ${site.photo_jpeg_quality || 0.72}`;
  if (!site.sync_window_enabled) return `${t('sites.upload.summary.always')} · ${size}`;
  return `${t('sites.upload.summary.window')} ${site.sync_window_start}-${site.sync_window_end} (${site.timezone}) · ${size}`;
}

// Sync window and photo size of one factory. Tablets pick the values up with the
// site data and keep the last known copy offline.
function SiteUploadSettings({ site, onSaved }) {
  const toast = useToast();
  const { t } = useLang();
  const [form, setForm] = useState({
    sync_window_enabled: !!site.sync_window_enabled,
    // No suggested times: a prefilled range got saved by people who only
    // flipped the switch. Both times have to be chosen.
    sync_window_start: site.sync_window_start || '',
    sync_window_end: site.sync_window_end || '',
    timezone: site.timezone || 'Europe/Warsaw',
    photo_max_side: site.photo_max_side || 1600,
    photo_jpeg_quality: site.photo_jpeg_quality || 0.72,
  });
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm(v => ({ ...v, [field]: e.target.value }));
  const windowIncomplete = form.sync_window_enabled
    && (!form.sync_window_start || !form.sync_window_end || form.sync_window_start === form.sync_window_end);

  const save = async () => {
    if (windowIncomplete) return;
    setSaving(true);
    try {
      await api.put(`/api/sites/${site.id}/upload-settings`, {
        ...form,
        sync_window_start: form.sync_window_start || null,
        sync_window_end: form.sync_window_end || null,
        photo_max_side: Number(form.photo_max_side),
        photo_jpeg_quality: Number(form.photo_jpeg_quality),
      });
      toast.success(t('sites.upload.saved'));
      onSaved();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontWeight: 600 }}>{t('sites.upload.title')} · {site.name}</div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div className={`toggle ${form.sync_window_enabled ? 'on' : ''}`} style={{ cursor: 'pointer' }}
          onClick={() => setForm(v => ({ ...v, sync_window_enabled: !v.sync_window_enabled }))} />
        <span>{t('sites.upload.window')}</span>
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('sites.upload.window.help')}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label className="form-label" style={{ flex: 1, minWidth: 110 }}>{t('sites.upload.start')}
          <input type="time" className="input-field" value={form.sync_window_start}
            disabled={!form.sync_window_enabled} onChange={set('sync_window_start')} />
        </label>
        <label className="form-label" style={{ flex: 1, minWidth: 110 }}>{t('sites.upload.end')}
          <input type="time" className="input-field" value={form.sync_window_end}
            disabled={!form.sync_window_enabled} onChange={set('sync_window_end')} />
        </label>
        <label className="form-label" style={{ flex: 2, minWidth: 160 }}>{t('sites.upload.timezone')}
          <input type="text" className="input-field" value={form.timezone} onChange={set('timezone')} />
        </label>
      </div>

      {windowIncomplete && (
        <div style={{ fontSize: '0.75rem', color: 'var(--accent-warning)' }}>{t('sites.upload.window.incomplete')}</div>
      )}

      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('sites.upload.photo.help')}</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label className="form-label" style={{ flex: 1, minWidth: 140 }}>{t('sites.upload.maxside')}
          <input type="number" className="input-field" min="640" max="6000" step="20"
            value={form.photo_max_side} onChange={set('photo_max_side')} />
        </label>
        <label className="form-label" style={{ flex: 1, minWidth: 140 }}>{t('sites.upload.quality')}
          <input type="number" className="input-field" min="0.4" max="0.95" step="0.01"
            value={form.photo_jpeg_quality} onChange={set('photo_jpeg_quality')} />
        </label>
      </div>

      <div>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || windowIncomplete}>
          {saving ? '...' : t('common.save')}
        </button>
      </div>
    </div>
  );
}

// ── Checklists Tab ────────────────────────────────────────────────────────────
function ChecklistsTab() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dept, setDept] = useState('production');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null); // { id, label }

  useEffect(() => { loadItems(); }, []);
  const loadItems = async () => {
    try { const { data } = await api.get('/api/checklists'); setItems(data); }
    catch {} finally { setLoading(false); }
  };

  const saveEdit = async () => {
    const label = editing.label.trim();
    if (!label || label.length > 60) return;
    try {
      await api.put(`/api/checklists/${editing.id}`, { label });
      toast.success('Item updated');
      setEditing(null);
      loadItems();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };

  const handleAdd = async () => {
    if (!newLabel.trim() || newLabel.length > 60) return;
    setAdding(true);
    try {
      await api.post('/api/checklists', { department: dept, label: newLabel.trim() });
      toast.success('Item added');
      setNewLabel('');
      loadItems();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setAdding(false); }
  };

  const toggleItem = async (item) => {
    try {
      await api.put(`/api/checklists/${item.id}`, { active: item.active ? 0 : 1 });
      loadItems();
    } catch { toast.error('Failed to update'); }
  };

  const filtered = items.filter(i => i.department === dept);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['production', 'rd'].map(d => (
          <button key={d} className={`btn btn-sm ${dept === d ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setDept(d)}>
            {d === 'production' ? 'Production' : 'R&D'}
          </button>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <SectionHeader title="Add Item" />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value.slice(0, 60))}
              className="input-field" placeholder="Checklist item label..."
              onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
            <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              {newLabel.length}/60
            </span>
          </div>
          <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !newLabel.trim()}>
            {adding ? '...' : 'Add'}
          </button>
        </div>
      </div>

      <div className="section-title">Items ({filtered.length})</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {filtered.map((item, i) => (
          <div key={item.id} className="card" style={{
            padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10,
            opacity: item.active ? 1 : 0.5
          }}>
            <div className="drag-handle">⋮⋮</div>
            {editing?.id === item.id ? (
              <>
                <input type="text" value={editing.label} autoFocus
                  onChange={e => setEditing(s => ({ ...s, label: e.target.value.slice(0, 60) }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(null); }}
                  className="input-field" style={{ flex: 1 }} />
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>Save</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>✕</button>
              </>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: '0.9375rem' }}>{item.label}</span>
                <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => setEditing({ id: item.id, label: item.label })}>✏️</button>
                <div className={`toggle ${item.active ? 'on' : ''}`} onClick={() => toggleItem(item)} style={{ cursor: 'pointer' }} />
              </>
            )}
          </div>
        ))}
        {filtered.length === 0 && <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>No items yet</div>}
      </div>

      <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(79,142,247,0.06)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
        💡 Changes take effect immediately. Historical checklist responses are not affected.
      </div>
    </div>
  );
}

// ── Performance Tab ───────────────────────────────────────────────────────────
function PerformanceTab() {
  const toast = useToast();
  const [clearing, setClearing] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildProgress, setRebuildProgress] = useState(0);

  const clearCache = async () => {
    setClearing(true);
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) await reg.update();
      }
      toast.success('App cache cleared. App shell will reload.');
    } catch { toast.error('Failed to clear cache'); }
    finally { setClearing(false); }
  };

  const rebuildIndex = async () => {
    setRebuilding(true);
    setRebuildProgress(0);
    try {
      for (let i = 0; i <= 100; i += 20) {
        await new Promise(r => setTimeout(r, 300));
        setRebuildProgress(i);
      }
      toast.success('Search index rebuilt');
    } finally { setRebuilding(false); setRebuildProgress(0); }
  };

  return (
    <div>
      <div className="warning-banner" style={{ marginBottom: 16 }}>
        ⚠ These operations affect performance only. No data will be deleted.
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <SectionHeader title="Clear App Cache" />
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 14 }}>
          Clears browser cache, Service Worker cache, and cached API responses. Does not affect photos or any data.
        </p>
        <button className="btn btn-secondary" onClick={clearCache} disabled={clearing}>
          {clearing ? 'Clearing...' : '🗑 Clear App Cache'}
        </button>
      </div>

      <div className="card">
        <SectionHeader title="Rebuild Search Index" />
        <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 14 }}>
          Drops and recreates database indexes. No data is deleted. May take a few minutes on large datasets.
        </p>
        {rebuilding && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ height: 4, background: 'var(--bg-tertiary)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: 'var(--accent-primary)',
                width: `${rebuildProgress}%`, transition: 'width 0.3s ease'
              }} />
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>{rebuildProgress}%</div>
          </div>
        )}
        <button className="btn btn-secondary" onClick={rebuildIndex} disabled={rebuilding}>
          {rebuilding ? `Rebuilding... ${rebuildProgress}%` : '⚙ Rebuild Search Index'}
        </button>
      </div>
    </div>
  );
}
